// src/utils/permissionDefaults.js
//
// Peça compartilhada dos moldes de permissão ("Público" / "Privado") que cada
// formulário de criação e edição monta por conta própria.
//
// Motivo de existir: quando quem cria é um membro gerenciado, o titular do
// vínculo precisa nascer com visualizar/selecionar/editar sobre o texto, a
// atividade ou a nota — nos DOIS moldes, não só no público. Sem isso o titular
// não enxerga o conteúdo que mandou o membro produzir, e o membro teria de
// lembrar de conceder a permissão na mão toda vez.

import { useUserStore } from "@/stores/useUserStore";
import { useGroupStore } from "@/stores/useGroupStore";

/**
 * Lê o claim `manager_id` (id do titular) do JWT guardado no localStorage.
 * Também usado por useMemberRestrictions — é a única cópia deste decode.
 */
export function decodeManagerUserId() {
  try {
    const token = localStorage.getItem("userToken");
    if (!token) return null;
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    const payload = JSON.parse(json);
    const id = parseInt(payload["manager_id"], 10);
    return Number.isFinite(id) ? id : null;
  } catch {
    return null;
  }
}

/**
 * ID do titular do contexto de login ATIVO, ou null quando o usuário não está
 * logado como membro gerenciado.
 *
 * Um mesmo usuário pode ser membro de vários titulares, mas a sessão vive num
 * contexto só — é o titular desse contexto que entra nos moldes. Logado como
 * titular (ou usuário comum), não há ninguém a acrescentar.
 */
export function getContextManagerUserId() {
  const currentUser = useUserStore().currentUser;
  if (currentUser?.loginContext !== "managed") return null;

  const managerId = decodeManagerUserId();
  if (managerId == null) return null;

  // Defensivo: vínculo de um usuário consigo mesmo não existe, mas se o claim
  // vier assim, duplicar as permissões do criador não ajuda ninguém.
  return managerId === currentUser?.userId ? null : managerId;
}

/**
 * O grupo "Todos" do círculo (IsCircleDefault), quando o usuário logado tem
 * um: titular com membros, ou membro logado no contexto do titular. É o alvo
 * do molde "Público" de quem tem círculo — e a prova de que existe um círculo.
 * Depende de groupStore.fetchGroups já ter rodado, o que os formulários fazem
 * antes de montar o molde inicial.
 */
export function getCircleDefaultGroup() {
  return (
    (useGroupStore().groups || []).find((g) => g.isCircleDefault === true) ??
    null
  );
}

export function hasCircle() {
  return getCircleDefaultGroup() != null;
}

/**
 * Modo de acesso com que um formulário de criação abre quando o usuário ainda
 * não escolheu nada nesta sessão.
 *
 * "private" para quem tem círculo — membro gerenciado ou titular de escola/
 * família. Para o titular, "Público" significa toda a escola: alunos e
 * professores veem e selecionam o que ele salvar. Abrir nesse modo fazia um
 * rascunho salvo sem mexer na visibilidade cair na estante de 300 alunos.
 * Publicar tem que ser um gesto deliberado.
 *
 * "public" só para quem não tem círculo — aí "público" é o acervo da
 * plataforma, o padrão histórico, e não há ninguém sob sua responsabilidade.
 */
export function getDefaultAccessMode() {
  return getContextManagerUserId() || hasCircle() ? "private" : "public";
}

/**
 * Texto de ajuda do modo "Público": o que ele alcança depende de haver
 * círculo, e a frase antiga ("todos os usuários da plataforma") passou a
 * mentir para escola e família depois da hierarquia de visibilidade.
 */
export function getPublicModeHint() {
  return hasCircle()
    ? "Todos do seu círculo (titular e membros ativos)"
    : "Todos os usuários da plataforma";
}

/**
 * Concessões de visualizar/selecionar/editar para o titular do contexto ativo.
 * Lista vazia quando não há titular ou quando algum id de permissão não foi
 * resolvido — mesma postura dos moldes, que omitem o que não conseguem montar.
 *
 * @param {{visualizarId: number|null, selecionarId: number|null, editarId: number|null}} permissionIds
 */
export function buildManagerPermissionGrants({
  visualizarId,
  selecionarId,
  editarId,
}) {
  const managerId = getContextManagerUserId();
  if (!managerId || !visualizarId || !selecionarId || !editarId) return [];

  return [
    { entityId: managerId, type: "user", permissionId: visualizarId },
    { entityId: managerId, type: "user", permissionId: selecionarId },
    { entityId: managerId, type: "user", permissionId: editarId },
  ];
}
